import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

import Select from 'components/ui/SelectField';
import { PageContainer, PageHeaderTitle, PageHeaderDetails } from '../common/Page';
import CtaPrimary from 'components/ui/CtaPrimary';
import Form from 'components/ui/Form';
import { media } from 'helpers/MediaHelper';

import { selectLeaseTerm } from '../../store/actions';

const StyledCtaPrimary = styled(CtaPrimary)`
  background-color: ${colors.LIGHT_BLUE_GREY};
  color: ${colors.ULTRAMARINE_BLUE};
  margin-top: 64px;
  width: fit-content;
  ${media.mobile`
    margin-top: 40px;
  `};
`;

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const LeaseTermsValidation = Yup.object().shape({
  termLength: Yup.string().required(getTranslatedString('required')),
});

const LengthOfLease = ({
  pageIndex,
  previousPageIndex,
  setPageIndex,
  lastPageIndex,
  selectedProposal,
  selectLeaseTerm,
  selectedLeaseTerm,
}) => {
  const [terms, setTerms] = useState([]);

  const moveForward = useCallback(() => {
    if (pageIndex !== lastPageIndex) {
      return setPageIndex(pageIndex + 1);
    }
  }, [setPageIndex, pageIndex, lastPageIndex]);

  const moveBackward = useCallback(() => {
    if (pageIndex !== lastPageIndex) {
      return setPageIndex(pageIndex - 1);
    }
  }, [setPageIndex, pageIndex, lastPageIndex]);

  const availableTerms = useCallback(
    (minTerm, maxTerm) => {
      const lengthOfTerm = maxTerm - minTerm + 1;
      if (!lengthOfTerm) {
        return [selectedProposal.min_term, selectedProposal.max_term].map((term) => ({
          label: term,
          value: term,
        }));
      }

      if (minTerm >= maxTerm) {
        return [
          {
            label: `${minTerm} ${getTranslatedString('landlordPortal.rentRoll.renewals.month')}`,
            value: minTerm,
          },
        ];
      }

      return Array(lengthOfTerm)
        .fill()
        .map((_, index) => {
          let monthVerbiage = getTranslatedString('landlordPortal.rentRoll.renewals.monthsPlural');
          if (minTerm + index === 1) {
            monthVerbiage = getTranslatedString('landlordPortal.rentRoll.renewals.month');
          }
          return {
            label: `${minTerm + index} ${monthVerbiage}`,
            value: `${minTerm + index}`,
          };
        });
    },
    [selectedProposal.max_term, selectedProposal.min_term]
  );

  useEffect(() => {
    if (selectedProposal.max_term - selectedProposal.min_term === 0) {
      if (pageIndex > previousPageIndex) {
        selectLeaseTerm(selectedProposal.max_term);
        moveForward();
      }
      if (pageIndex < previousPageIndex) {
        moveBackward();
      }
      return;
    }
    const terms = availableTerms(selectedProposal.min_term, selectedProposal.max_term);
    setTerms(terms);
  }, [
    availableTerms,
    moveForward,
    moveBackward,
    selectLeaseTerm,
    selectedProposal.max_term,
    selectedProposal.min_term,
    pageIndex,
    previousPageIndex,
  ]);

  return (
    <PageContainer>
      <Formik
        initialValues={{
          termLength: selectedLeaseTerm || undefined,
        }}
        validationSchema={LeaseTermsValidation}
        onSubmit={(values) => {
          selectLeaseTerm(values.termLength);
          moveForward();
        }}
      >
        {({ values, setFieldValue, submitForm, errors, touched }) => (
          <React.Fragment>
            <PageHeaderTitle>
              {getTranslatedString('landlordPortal.rentRoll.renewals.selectDesiredLength')}
            </PageHeaderTitle>
            <PageHeaderDetails>
              {getTranslatedString('landlordPortal.rentRoll.renewals.beforeWeContinue')}
            </PageHeaderDetails>
            <Form.Item
              label="Lease Length"
              validateStatus={touched.termLength && errors.termLength ? 'error' : null}
              help={touched.termLength && errors.termLength}
            >
              <FullWidthSelect
                name="termLength"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.termLength}
                onChange={(value) => setFieldValue('termLength', value)}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {terms.map((term) => (
                  <Select.Option value={term.value} key={term.value}>
                    {term.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>

            <StyledCtaPrimary type="submit" onClick={() => submitForm()}>
              {getTranslatedString('continue')}
            </StyledCtaPrimary>
          </React.Fragment>
        )}
      </Formik>
    </PageContainer>
  );
};

const mapStateToProps = (store) => ({
  selectedLeaseTerm: store.landlord.renewals.selectedLeaseTerm,
  selectedProposal: store.landlord.renewals.selectedProposal,
});

const mapDispatchToProps = {
  selectLeaseTerm,
};

export default connect(mapStateToProps, mapDispatchToProps)(LengthOfLease);
